// SLIXZYWRLD iOS release-surface contract.
// Release narrowing is a renderer/distribution decision only. It never changes canonical
// Person/WRLD/object law and it must not manufacture another mobile identity.

export const IOS_RELEASE_BLOCKED_ROOTS=Object.freeze([
 '/community',
 '/media',
 '/market',
 '/assets',
 '/build',
 '/people',
 '/internal',
]);

export const IOS_RELEASE_SURFACE_ID='MOBILE_APP';
export const IOS_RELEASE_SAFE_FALLBACK='/';

export function normalizeIOSReleasePath(input='/'){
 const raw=String(input||'/').trim()||'/';
 const withSlash=raw.startsWith('/')?raw:`/${raw}`;
 return withSlash.replace(/\/{2,}/g,'/').replace(/\/+$/,'')||'/';
}

export function isIOSReleaseRestrictedPath(input='/'){
 const path=normalizeIOSReleasePath(input);
 return IOS_RELEASE_BLOCKED_ROOTS.some(root=>path===root||path.startsWith(`${root}/`));
}

export function detectIOSRelease({env={},runtime=globalThis}={}){
 if(String(env?.VITE_SLIXZYWRLD_IOS_RELEASE||'')==='1')return true;
 const cap=runtime?.Capacitor;
 try{return Boolean(cap?.isNativePlatform?.()&&cap?.getPlatform?.()==='ios')}catch{return false}
}

export function resolveIOSReleasePath(input='/',{enabled=true}={}){
 const path=normalizeIOSReleasePath(input);
 return enabled&&isIOSReleaseRestrictedPath(path)?IOS_RELEASE_SAFE_FALLBACK:path;
}

function pathnameFromTarget(target,baseHref){
 if(target==null)return null;
 try{return new URL(String(target),baseHref).pathname}catch{return null}
}

export function installIOSReleaseNavigationGuard({env=(import.meta.env||{}),runtime=globalThis}={}){
 const enabled=detectIOSRelease({env,runtime});
 if(!enabled)return Object.freeze({enabled:false,blocked:false,path:normalizeIOSReleasePath(runtime?.location?.pathname||'/')});
 runtime.__SLIXZYWRLD_IOS_RELEASE__=true;
 runtime.__SLIXZYWRLD_SURFACE_ID__=IOS_RELEASE_SURFACE_ID;
 const current=normalizeIOSReleasePath(runtime?.location?.pathname||'/');
 const blocked=isIOSReleaseRestrictedPath(current);
 const safePath=resolveIOSReleasePath(current,{enabled:true});
 const history=runtime?.history,location=runtime?.location;
 if(history&&location&&!history.__slixzywrldIOSReleaseGuard){
  const baseHref=location.href||'https://slixzywrld-website.vercel.app/';
  const originalPush=history.pushState.bind(history),originalReplace=history.replaceState.bind(history);
  history.pushState=(state,title,url)=>{
   const candidate=pathnameFromTarget(url,baseHref);
   return originalPush(state,title,candidate&&isIOSReleaseRestrictedPath(candidate)?IOS_RELEASE_SAFE_FALLBACK:url);
  };
  history.replaceState=(state,title,url)=>{
   const candidate=pathnameFromTarget(url,baseHref);
   return originalReplace(state,title,candidate&&isIOSReleaseRestrictedPath(candidate)?IOS_RELEASE_SAFE_FALLBACK:url);
  };
  Object.defineProperty(history,'__slixzywrldIOSReleaseGuard',{value:true,configurable:false});
 }
 const doc=runtime?.document;
 // iOS v1 deliberately excludes user-to-user communication/discovery until the native
 // Report / Block / moderation operating loop is evidence-verified. This only narrows the
 // renderer; it does not delete or reinterpret canonical relationship/history objects.
 if(doc?.documentElement)doc.documentElement.dataset.slixzywrldIosRelease='1';
 if(doc&&!doc.getElementById?.('slixzywrld-ios-v1-surface-guard')){
  const style=doc.createElement?.('style');
  if(style){style.id='slixzywrld-ios-v1-surface-guard';style.textContent='.live-comms{display:none!important}';doc.head?.appendChild?.(style)}
 }
 if(doc&&!doc.__slixzywrldIOSReleaseGuard){
  doc.addEventListener('click',event=>{
   const anchor=event.target?.closest?.('a[href]');
   if(!anchor)return;
   const candidate=pathnameFromTarget(anchor.href,runtime.location?.href||'https://slixzywrld-website.vercel.app/');
   if(!candidate||!isIOSReleaseRestrictedPath(candidate))return;
   event.preventDefault();
   if(runtime.history?.replaceState){runtime.history.replaceState({},'',IOS_RELEASE_SAFE_FALLBACK);runtime.dispatchEvent?.(new PopStateEvent('popstate'))}
  },true);
  Object.defineProperty(doc,'__slixzywrldIOSReleaseGuard',{value:true,configurable:false});
 }
 if(blocked&&history?.replaceState)history.replaceState({},'',IOS_RELEASE_SAFE_FALLBACK);
 return Object.freeze({enabled:true,blocked,path:safePath});
}
